import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  TemplateRef
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, skip, Subscription, shareReplay } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

import { Client } from 'src/app/models/client';
import { Activity, ActivityModel } from 'src/app/models/activities';

import {
  CommonTableComponent,
  TableColumn
} from 'src/app/components/common/common-table/common-table.component';
import { CommonDropdownData } from 'src/app/components/common/common-dropdown/common-dropdown.component.model';
import { MapDataRequest } from 'src/app/components/arcgis/basic-esri-map/basic-esri-map.component';
import { MapExtent } from 'src/app/models/mapextent';
import { GeomUtils } from 'src/app/components/arcgis/geom-utils';
import { ValidationService } from 'src/app/services';
import { WorkflowContextService } from 'src/app/services/workflow-context.service';
import { WorkflowService } from 'src/app/services/workflow.service';
import { InspectionsActionMenuComponent } from '../inspections-action-menu/inspections-action-menu.component';
import { InspectionsJanusViewDetailsComponent } from '../inspections-janus-view-details/inspections-janus-view-details.component';
import { JsonNetDecycle } from 'src/app/services/utilities/json-net-decycle';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption,
  ItemSearchUtils
} from 'src/app/components/filter-list/models/filterClasses';
import { InspectionsJanusService } from '../inspections-janus.service';
import { CommonPaginationData } from 'src/app/components/common/common-pagination/common-pagination.component.model';
import { ColumnOption } from 'src/app/models/grid-settings';
import { InspectionStep } from 'src/app/models/activities';

@Component({
  selector: 'app-assigned-inspections-janus',
  standalone: false,
  templateUrl: './assigned-inspections-janus.component.html',
  styleUrl: './assigned-inspections-janus.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AssignedInspectionsJanusComponent implements OnInit, OnDestroy {
  @Input() public id: string = 'assigned-inspections';
  @Input() public client!: Client;
  @Input() public sortField: string = 'inspectionAppNumber';
  @Input() public sortDescending: boolean = true;

  @Output() private dataLoaded = new EventEmitter<string>();
  @Output() recordCountSet = new EventEmitter<number>();

  @ViewChild(CommonTableComponent) table?: CommonTableComponent;
  @ViewChild('inspectionNoTemplate', { static: true })
  inspectionNoTemplate!: TemplateRef<any>;

  data: any[] = [];
  filteredData: any[] = [];
  pagedData: any[] = [];
  columns: TableColumn[] = [];
  expandedComponent: any = null;
  expandedComponentInputs: any;

  viewMode: 'list' | 'map' = 'list';
  selectedInspectorId = 'ALL';
  inspectorOptions: Array<{ id: string; label: string }> = [];
  inspectorDropdownData: CommonDropdownData = {
    placeholder: 'INSPECTOR',
    options: [],
    selectedValue: null,
    style: {
      width: '208px'
    },
    className: 'assigned-inspector-dropdown'
  };

  currentPage = 0;
  pageSize = 10;
  readonly pageSizeOptions: number[] = [10, 25, 100, 200];
  totalPages = 0;
  totalRecords = 0;
  baseTotalRecords = 0;
  isLoading = false;
  isSorting = false;
  public columnOptions: ColumnOption[] = [];
  public exportColumns: string[] = [];

  mapFilters$ = new BehaviorSubject<any>(null);
  mapPopupActions = [
    {
      title: 'View Application',
      id: 'view-application',
      className: 'esri-icon-zoom-out-magnifying-glass',
      handler: (item: any) => {
        if (!item?.id) {
          return;
        }
        this._workflowSvc.getInspectionJanus(item.id).subscribe(
          (inspectionDetails: any) => {
            if (inspectionDetails?.inspectionApplicationId) {
              this._router.navigate([
                `/application/workflow-application/${inspectionDetails.inspectionApplicationId}`
              ]);
            }
          },
          error => {
            console.error(
              'Failed to fetch inspection details for navigation',
              error
            );
            this.toast.error('Failed to load application details.');
          }
        );
      }
    },
    GeomUtils.CopyCoordinatesToClipboardAction,
    GeomUtils.NavigateToGoogleAction,
    GeomUtils.NavigateToBingAction
  ];

  private isDestroyed = false;
  private q?: MapDataRequest;
  private currentMapExtent: MapExtent | null = null;
  private workflowApplicationId!: string;
  private filterSub?: Subscription;
  private readonly SORT_CACHE_KEY = 'datatable-assigned-inspections-sort';
  private readonly UI_STATE_CACHE_KEY = 'assigned-inspections-ui-state';
  private isFetching = false;
  private activeSearchKey = '';
  private lastCompletedSearchKey = '';
  private activeInlineRow: any = null;

  private pendingDeepLinkRegistrationId: string | null = null;
  private pendingDeepLinkView: string | null = null;
  private deepLinkLookupId: string | null = null;
  // True while the table shows only the inspection from the URL instead of the paged list.
  private linkedOnly = false;
  private readonly DEEP_LINK_TABS = ['documents', 'schedule', 'complete', 'notes', 'LinktoApplication', 'view-summary'];

  public activity!: Activity<ActivityModel>;
  public activityForm!: UntypedFormGroup;
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-insections-assigned-janus';
  constructor(
    private _workflowSvc: WorkflowService,
    private _context: WorkflowContextService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _fb: UntypedFormBuilder,
    private ref: ChangeDetectorRef,
    private toast: ToastrService,
    private inspectionsJanusSvc: InspectionsJanusService
  ) {}

  ngOnInit(): void {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);
    if (storedPageSize) {
      const pageSize = Number(storedPageSize);
      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }

    this.restoreSortFromCache();
    this.restoreUiStateFromCache();

    this._route.queryParamMap.subscribe(params => {
      const pageParam = params.get('p');
      const page = Number(pageParam ?? 1);
      const normalizedPage = Number.isNaN(page) || page < 1 ? 1 : page;
      this.currentPage = normalizedPage - 1;

      const id = params.get('id');
      const view = params.get('view');
      this.pendingDeepLinkRegistrationId = id;
      this.pendingDeepLinkView = view;

      if (!id || !view) {
        this.clearInlineState();
        if (this.linkedOnly) {
          this.linkedOnly = false;
          this.fetchInspections(this.currentPage, true);
        }
      } else if (!this.isFetching && this.data.length && this.activeInlineRow?.id !== id) {
        // Route reused (in-app link / back-forward): open without waiting for a reload.
        this.applyDeepLink(this.data);
      }

      const sortField = params.get('sf');
      const sortDesc = params.get('sd');
      if (sortField) this.sortField = sortField;
      if (sortDesc !== null) this.sortDescending = sortDesc === '1';

      const vm = params.get('vm') as 'list' | 'map';
      if (vm === 'list' || vm === 'map') {
        this.viewMode = vm;
      }

      const inspId = params.get('inspId');
      if (inspId) {
        this.selectedInspectorId = inspId;
        this.inspectorDropdownData = {
          ...this.inspectorDropdownData,
          selectedValue: this.selectedInspectorId === 'ALL' ? null : this.selectedInspectorId
        };
      }
    });

    this.setupColumns();
    const data = this.inspectionsJanusSvc.getData();
    this.inspectionsJanusSvc.setData({
      ...data,
      columns: this.columns,
      getExportOptions: (useExcelFormat: boolean) =>
        this.getExportOptions(useExcelFormat)
    });

    this.syncInspectorFromToolbarFilters(this.inspectionsJanusSvc.getData());
    this.linkedOnly = !!(this.pendingDeepLinkRegistrationId && this.pendingDeepLinkView);
    this.initializeData();

    this.filterSub = this.inspectionsJanusSvc.data$
      .pipe(skip(1))
      .subscribe(data => {
        this.syncInspectorFromToolbarFilters(data);
        if (!data.reRunInspectionsTable) {
          return;
        }

        this.exitLinkedOnly();

        this.inspectionsJanusSvc.setData({
          ...data,
          reRunInspectionsTable: false,
          getExportOptions: (useExcelFormat: boolean) =>
            this.getExportOptions(useExcelFormat)
        });

        // Pass this.currentPage instead of 0 to persist page on parent refresh
        this.fetchInspections(this.currentPage, true);
        this.emitMapRefresh();
      });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.setSelectedInspectorId('ALL');
    localStorage.removeItem(this.UI_STATE_CACHE_KEY);
    this.ref.detach();
    this.filterSub?.unsubscribe();
  }

  private syncSharedStateToRoute(): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        p: this.currentPage + 1,
        sf: this.sortField,
        sd: this.sortDescending ? '1' : '0',
        vm: this.viewMode,
        inspId: this.selectedInspectorId
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  setViewMode(mode: 'list' | 'map'): void {
    if (this.viewMode === mode) {
      return;
    }

    this.viewMode = mode;
    this.persistUiStateToCache();
    this.syncSharedStateToRoute();
    this.emitMapRefresh();
    if (mode === 'map' && !this.data.length) {
      this.fetchInspections(this.currentPage);
    }
  }

  onInspectorChange(inspectorId: string | number | null): void {
    this.exitLinkedOnly();
    this.setSelectedInspectorId(inspectorId ? String(inspectorId) : 'ALL');
    this.persistUiStateToCache();
    this.currentPage = 0;
    this.syncSharedStateToRoute();
    this.fetchInspections(0, true);
    this.emitMapRefresh();
    this.detectChanges();
  }

  onSortChanged(event: any): void {
    if (
      this.sortField === event.sortField &&
      this.sortDescending === event.sortDescending
    ) {
      return;
    }

    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;
    this.isSorting = true;
    localStorage.setItem(
      this.SORT_CACHE_KEY,
      JSON.stringify({
        sortField: this.sortField,
        sortDescending: this.sortDescending
      })
    );
    this.syncSharedStateToRoute();
    this.fetchInspections(this.currentPage, true);
  }

  onMapDataRequested(q: MapDataRequest): void {
    this.q = q;
    this.currentMapExtent = q.ext;
    this.loadMapInspections();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.syncSharedStateToRoute();
    this.fetchInspections(page);
  }

  onRowsPerPageChange(size: number): void {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));

    this.pageSize = pageSize;
    this.currentPage = 0;
    this.syncSharedStateToRoute();
    this.fetchInspections(0);
    this.emitMapRefresh();
  }

  goToFirstPage(): void {
    this.onPageChange(0);
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.onPageChange(this.currentPage + 1);
    }
  }

  goToLastPage(): void {
    if (this.totalPages > 0) {
      this.onPageChange(this.totalPages - 1);
    }
  }

  get paginationData(): CommonPaginationData {
    return {
      variant: 'staff',
      totalPages: this.totalPages,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      showRowsPerPage: true,
      showTotalRecords: true,
      visiblePages: this.getVisiblePages(this.totalPages, this.currentPage),
      canGoPrevious: this.currentPage > 0,
      totalRecords: this.totalRecords,
      canGoNext: this.totalPages > 0 && this.currentPage < this.totalPages - 1
    };
  }

  private getVisiblePages(
    totalPages: number,
    currentPage: number
  ): (number | 'dots')[] {
    const maxVisible = 5;
    if (!totalPages || totalPages <= 1) {
      return [];
    }

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages: (number | 'dots')[] = [];
    const start = Math.max(0, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    if (start > 0) {
      pages.push(0);
      if (start > 1) {
        pages.push('dots');
      }
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      if (end < totalPages - 2) {
        pages.push('dots');
      }
      pages.push(totalPages - 1);
    }

    return pages;
  }

  setupColumns() {
    this.columns = [
      {
        key: 'inspectionAppNumber',
        apiKey: 'inspectionAppNumber',
        label: 'Inspection #',
        alwaysVisible: true,
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.inspectionNoTemplate
      } as any,
      {
        key: 'appNo',
        apiKey: 'appNumber',
        label: 'App #',
        alwaysVisible: true,
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'applicant',
        apiKey: 'applicantName',
        label: 'Applicant',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'appType',
        apiKey: 'workflowName',
        label: 'App Type',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'appDate',
        apiKey: 'appCreatedOn',
        label: 'App Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'applicantPhone',
        apiKey: 'applicantPhone',
        label: 'Applicant Phone',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        visible: false
      } as any,
      {
        key: 'parcelId',
        apiKey: 'parcelId',
        label: 'Parcel ID',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        visible: false
      } as any,

      {
        key: 'inspectionCompleted',
        apiKey: 'inspectionCompletedOn',
        label: 'Inspection Completed Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'inspector',
        apiKey: 'inspectorId',
        label: 'Inspector',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'inspectionType',
        apiKey: 'inspectionType',
        label: 'Inspection Type',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'status',
        apiKey: 'inspectionStatus',
        label: 'Status',
        headerType: 'text',
        cellType: 'text'
      } as any,
      // Default Hidden per Jira requirement
      {
        key: 'description',
        apiKey: 'appDescription',
        label: 'Description',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        visible: false
      } as any,
      {
        key: 'requestedOnDate',
        apiKey: 'requestedOn',
        label: 'Requested Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'scheduledOnDate',
        apiKey: 'scheduledOn',
        label: 'Scheduled Date',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'scheduledOnTime',
        apiKey: 'scheduledOnTime',
        label: 'Scheduled Time',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'address',
        apiKey: 'address',
        label: 'Address',
        headerType: 'text',
        cellType: 'text'
      } as any,
      {
        key: 'city',
        apiKey: 'city',
        label: 'City',
        headerType: 'text',
        cellType: 'text'
      } as any,

      {
        key: 'action',
        apiKey: '',
        label: 'View',
        alwaysVisible: true,
        headerType: 'text',
        cellType: 'component',
        hideMenu: true,
        sortable: false,
        component: InspectionsActionMenuComponent,
        componentInputs: (row: any) => {
          return {
            row,
            viewDetails: (r: any) => {
              this.openInline(r, 'documents');
              this._router.navigate([], {
                relativeTo: this._route,
                queryParams: { p: this.currentPage + 1, view: 'documents', id: r.id },
                queryParamsHandling: 'merge'
              });
            }
          };
        }
      } as any
    ];
  }

  private initializeData() {
    this.loadInspectors();
    this.fetchInspections(this.currentPage, true);
  }

  private getMergedFilters(): any[] {
    const serviceData = this.inspectionsJanusSvc.getData();

    const uiFilters = [
      ...(serviceData.filterInputs || []),
      ...(serviceData.attributeFilterInputs || [])
    ].filter((f: any) => {
      const key = String(f?.id || f?.field || f?.name || f?.title || '')
        .trim()
        .toLowerCase();
      // Drop any existing inspector and inspectionMyWork parameters so we can safely inject them
      return (
        key !== 'inspectionmywork' &&
        key !== 'inspectorid' &&
        key !== 'inspector'
      );
    });

    // FIX: Use null as any to prevent static field mapping corruption that drops attribute filters
    let merged =
      uiFilters.length > 0
        ? ItemSearchUtils.mergeFilters(uiFilters, null as any)
        : [];

    // Safely catch instances where mergeFilters throws a single object or a null to prevent exception crashes
    if (!Array.isArray(merged)) {
      merged = merged ? [merged] : [];
    }

    // Group 2: The dynamically structured JSON properties (Append AFTER merge to prevent mapping corruption)
    if (this.selectedInspectorId && this.selectedInspectorId !== 'ALL') {
      merged.push(
        new ItemSearchOptionField({
          title: 'inspectorId',
          options: [
            new ItemSearchOptionFieldOption({
              title: '',
              selected: true,
              filterText: '',
              searchText:
                this.selectedInspectorId === 'NULL'
                  ? 'NULL'
                  : this.selectedInspectorId,
              strOperator: 'is'
            })
          ]
        })
      );
    }

    const searchTextFilters = serviceData.searchText || [];
    if (searchTextFilters.length > 0) {
      const explicitFields = uiFilters.map((f: any) =>
        String(f?.id || f?.field || f?.name || f?.title || '')
          .trim()
          .toLowerCase()
      );
      if (this.selectedInspectorId && this.selectedInspectorId !== 'ALL') {
        explicitFields.push('inspectorid');
      }

      // Strip explicitly selected fields to prevent backend parameter conflicts
      const sanitizedSearchTextFilters = searchTextFilters.filter((f: any) => {
        const key = String(f?.title || f?.id || f?.field || f?.name || '')
          .trim()
          .toLowerCase();
        return !explicitFields.includes(key);
      });
      merged.push(...sanitizedSearchTextFilters);
    }

    return merged;
  }

  fetchInspections(page: number, force: boolean = false): void {
    if (this.linkedOnly && this.pendingDeepLinkRegistrationId) {
      this.loadLinkedInspection();
      return;
    }

    this.currentPage = page;
    const mergedFilters = this.getMergedFilters();

    const requestPage = this.currentPage;
    const requestSize = this.pageSize;
    const cacheKey = this.buildSearchCriteriaKey(mergedFilters, requestPage);
    const searchKey = this.buildSearchKey(
      mergedFilters,
      requestPage,
      requestSize
    );

    if (this.isFetching && this.activeSearchKey === searchKey) return;
    if (!force && this.restoreCachedResult(cacheKey)) return;
    if (
      !force &&
      !this.isSorting &&
      this.lastCompletedSearchKey === searchKey &&
      this.data.length
    )
      return;

    this.isFetching = true;
    this.activeSearchKey = searchKey;

    if (!this.isSorting) this.isLoading = true;
    this.detectChanges();

    this._workflowSvc
      .searchInspectionsJanus(
        this.sortField,
        this.sortDescending,
        mergedFilters,
        requestPage,
        requestSize,
        null as any
      )
      .subscribe({
        next: (result: any) => {
          this.baseTotalRecords = result?.recordCount ?? 0;

          const mappedRows = (result?.inspections || []).map((rawInsp: any) =>
            this.normalizeInspection(rawInsp)
          );

          this.totalRecords = this.baseTotalRecords;
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize);

          this.data = mappedRows;
          this.filteredData = [...mappedRows];
          this.pagedData = [...mappedRows];

          if (this.pendingDeepLinkRegistrationId && this.pendingDeepLinkView) {
            const row = mappedRows.find((r: any) => r.id === this.pendingDeepLinkRegistrationId);
            if (row) {
              setTimeout(() => this.openInline(row, this.getDeepLinkTab()));
            } else {
              this.closeInlineRoute();
            }
          } else if (this.activeInlineRow && this.table) {
            const currentId = this.activeInlineRow.id;
            const updatedRow = mappedRows.find((r: any) => r.id === currentId);
            if (updatedRow) {
              this.activeInlineRow = updatedRow;
              setTimeout(() => {
                if (this.table) {
                  this.table.expandRow(updatedRow);
                }
              });
            } else {
              this.clearInlineState();
            }
          }

          for (const ac of this.getExportableColumns()) {
            if (
              !this.columnOptions.find(co => co.name === (ac as any).apiKey)
            ) {
              this.columnOptions.push(
                new ColumnOption({
                  label: ac.label || (ac as any).apiKey,
                  name: (ac as any).apiKey as string,
                  includeInExport: true
                })
              );
            }
          }
          this.exportColumns = this.columnOptions
            .filter(co => co.includeInExport)
            .map(co => co.name);

          const currentData = this.inspectionsJanusSvc.getData();
          if (
            JSON.stringify(currentData.columnOptions) !==
              JSON.stringify(this.columnOptions) ||
            JSON.stringify(currentData.exportOptions) !==
              JSON.stringify(this.exportColumns)
          ) {
            this.inspectionsJanusSvc.setData({
              ...currentData,
              columnOptions: this.columnOptions,
              exportOptions: this.exportColumns,
              getExportOptions: (useExcelFormat: boolean) =>
                this.getExportOptions(useExcelFormat)
            });
          } else if (!currentData.getExportOptions) {
            this.inspectionsJanusSvc.setData({
              ...currentData,
              getExportOptions: (useExcelFormat: boolean) =>
                this.getExportOptions(useExcelFormat)
            });
          }

          this.recordCountSet.emit(this.totalRecords);
          this.dataLoaded.emit('AssignedInspectionsJanusComponent');
          this.cacheCurrentResult(cacheKey);

          if (this.q) {
            this.emitMapDataFromCurrentRows(this.q);
            this.q = undefined;
          }

          this.isLoading = false;
          this.isSorting = false;
          this.isFetching = false;
          this.lastCompletedSearchKey = this.activeSearchKey;
          this.activeSearchKey = '';
          this.detectChanges();
        },
        error: err => {
          console.error(
            '[Assigned Inspections Janus] Failed to load inspections',
            err
          );
          this.data = [];
          this.filteredData = [];
          this.pagedData = [];
          this.totalRecords = 0;
          this.totalPages = 0;
          this.currentPage = 0;
          this.baseTotalRecords = 0;
          this.isLoading = false;
          this.isSorting = false;
          this.isFetching = false;
          this.activeSearchKey = '';
          if (this.q) {
            this.emitMapDataFromCurrentRows(this.q);
            this.q = undefined;
          }
          this.detectChanges();
        }
      });
  }

  private loadMapInspections(): void {
    if (!this.q) return;

    const mergedFilters = this.getMergedFilters();
    const mapExtent = this.q.ext;
    const request = this.q;
    const initialSize =
      this.baseTotalRecords > 0
        ? Math.max(this.baseTotalRecords, this.pageSize)
        : this.pageSize;

    const runMapSearch = (
      requestSize: number,
      allowRetryWithRecordCount: boolean
    ): void => {
      this._workflowSvc
        .searchInspectionsJanus(
          this.sortField,
          this.sortDescending,
          mergedFilters,
          0,
          requestSize,
          mapExtent
        )
        .subscribe({
          next: (result: any) => {
            const recordCount = result?.recordCount ?? 0;
            const resultRowsCount = Array.isArray(result?.inspections)
              ? result.inspections.length
              : 0;

            if (
              allowRetryWithRecordCount &&
              recordCount > resultRowsCount &&
              requestSize < recordCount
            ) {
              runMapSearch(recordCount, false);
              return;
            }

            let rows = (result?.inspections || []).map((rawInsp: any) =>
              this.normalizeInspection(rawInsp)
            );

            let mapData = rows
              .filter(
                (row: any) =>
                  Array.isArray(row.geometryWkts) && row.geometryWkts.length > 0
              )
              .map((row: any) => this.toMapData(row));

            if (mapData.length === 0) {
              mapData = (this.pagedData || [])
                .filter(
                  (row: any) =>
                    Array.isArray(row.geometryWkts) &&
                    row.geometryWkts.length > 0
                )
                .map((row: any) => this.toMapData(row));
            }

            request.gimmeTheData$.emit(mapData);
            if (this.q === request) this.q = undefined;
          },
          error: (err: any) => {
            console.error(
              '[Assigned Inspections Janus] Failed to load map inspections',
              err
            );
            this.emitMapDataFromCurrentRows(request);
            if (this.q === request) this.q = undefined;
          }
        });
    };
    runMapSearch(initialSize, true);
  }

  private loadInspectors(): void {
    const clientId = this.client?.id || this._context.client?.id || '';
    const cacheKey = `_cachedInspectorIds_${clientId}`;

    if (!(this._workflowSvc as any)[cacheKey]) {
      (this._workflowSvc as any)[
        cacheKey
      ] = this._workflowSvc
        .getUniqueInspectorIds(clientId)
        .pipe(shareReplay(1));
    }

    (this._workflowSvc as any)[cacheKey].subscribe({
      next: (result: any) => {
        const inspectorMap: Record<string, string> = { ...(result || {}) };
        inspectorMap['NULL'] = 'None';

        const entries = Object.entries(inspectorMap)
          .filter(([id, name]) => !!id && !!name)
          .sort((a, b) => {
            const aId = String(a[0] || '')
              .trim()
              .toUpperCase();
            const bId = String(b[0] || '')
              .trim()
              .toUpperCase();
            if (aId === 'NULL') {
              return -1;
            }
            if (bId === 'NULL') {
              return 1;
            }
            return String(a[1]).localeCompare(String(b[1]));
          });

        this.inspectorOptions = entries.map(([id, name]) => ({
          id,
          label:
            String(id)
              .trim()
              .toUpperCase() === 'NULL'
              ? 'None'
              : String(name)
        }));
        this.inspectorDropdownData = {
          ...this.inspectorDropdownData,
          options: this.inspectorOptions.map(i => ({
            label: i.label,
            value: i.id
          })),
          selectedValue:
            this.selectedInspectorId === 'ALL' ? null : this.selectedInspectorId
        };

        this.detectChanges();
      },
      error: () => {
        this.inspectorOptions = [];
        this.inspectorDropdownData = {
          ...this.inspectorDropdownData,
          options: []
        };
        this.detectChanges();
      }
    });
  }

  private exitLinkedOnly(): void {
    if (!this.linkedOnly) return;
    this.linkedOnly = false;
    this.closeInlineRoute();
  }

  private getDeepLinkTab(): string {
    // Older links used view=details, which is not a tab.
    const view = this.pendingDeepLinkView || '';
    return this.DEEP_LINK_TABS.includes(view) ? view : 'documents';
  }

  private applyDeepLink(rows: any[]): void {
    const row = rows.find((r: any) => r.id === this.pendingDeepLinkRegistrationId);
    if (row) {
      setTimeout(() => this.openInline(row, this.getDeepLinkTab()));
    } else {
      this.loadLinkedInspection();
    }
  }

  private loadLinkedInspection(): void {
    const id = this.pendingDeepLinkRegistrationId;
    if (!id || !this.pendingDeepLinkView || this.deepLinkLookupId === id) return;

    this.linkedOnly = true;
    this.deepLinkLookupId = id;
    this.isLoading = true;
    this.detectChanges();

    // Look the inspection up by id so the user's saved filters, sort and page don't hide it.
    const idFilter = new ItemSearchOptionField({
      title: 'inspectionId',
      options: [
        new ItemSearchOptionFieldOption({
          title: '',
          selected: true,
          filterText: '',
          searchText: id,
          strOperator: 'is'
        })
      ]
    });

    this._workflowSvc
      .searchInspectionsJanus(this.sortField, this.sortDescending, [idFilter], 0, 1, null as any)
      .subscribe({
        next: (result: any) => {
          this.deepLinkLookupId = null;
          if (this.pendingDeepLinkRegistrationId !== id) return;

          this.isLoading = false;
          const raw = (result?.inspections || []).find((r: any) => r?.inspectionId === id);
          if (!raw) {
            this.toast.warning('The requested inspection was not found or is not available in this department.');
            // Removing id from the URL drops linkedOnly and loads the normal list.
            this.closeInlineRoute();
            return;
          }

          const row = this.normalizeInspection(raw);
          this.data = [row];
          this.filteredData = [row];
          this.pagedData = [row];
          this.currentPage = 0;
          this.totalRecords = 1;
          this.baseTotalRecords = 1;
          this.totalPages = 1;
          this.recordCountSet.emit(this.totalRecords);
          this.dataLoaded.emit('AssignedInspectionsJanusComponent');
          this.detectChanges();

          if (this.activeInlineRow?.id === id && this.expandedComponent) {
            this.activeInlineRow = row;
            setTimeout(() => this.table?.expandRow(row));
          } else {
            setTimeout(() => this.openInline(row, this.getDeepLinkTab()));
          }
        },
        error: (err: any) => {
          this.deepLinkLookupId = null;
          this.isLoading = false;
          console.error('[Assigned Inspections Janus] Failed to load linked inspection', err);
          this.toast.error('Failed to load the requested inspection.');
          this.closeInlineRoute();
        }
      });
  }

  private buildSearchKey(
    filters: any[],
    requestPage: number,
    requestSize: number
  ): string {
    return JSON.stringify({
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      filters: filters || [],
      selectedInspectorId: this.selectedInspectorId,
      requestPage,
      requestSize
    });
  }

  private buildSearchCriteriaKey(filters: any[], requestPage: number): string {
    return JSON.stringify({
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      filters: filters || [],
      selectedInspectorId: this.selectedInspectorId,
      requestPage,
      pageSize: this.pageSize
    });
  }

  private cacheCurrentResult(cacheKey: string): void {
    const currentData = this.inspectionsJanusSvc.getData();
    this.inspectionsJanusSvc.setData({
      ...currentData,
      assignedInspectionsCache: {
        cacheKey,
        data: [...this.data],
        filteredData: [...this.filteredData],
        pagedData: [...this.pagedData],
        totalRecords: this.totalRecords,
        totalPages: this.totalPages,
        baseTotalRecords: this.baseTotalRecords,
        currentPage: this.currentPage,
        pageSize: this.pageSize
      }
    });
  }

  private restoreCachedResult(cacheKey: string): boolean {
    const cache = this.inspectionsJanusSvc.getData().assignedInspectionsCache;
    if (!cache || cache.cacheKey !== cacheKey) {
      return false;
    }

    this.data = [...(cache.data || [])];
    this.filteredData = [...(cache.filteredData || [])];
    this.pagedData = [...(cache.pagedData || [])];
    this.totalRecords = cache.totalRecords || 0;
    this.totalPages = cache.totalPages || 0;
    this.baseTotalRecords = cache.baseTotalRecords || 0;
    this.currentPage = cache.currentPage || 0;
    this.pageSize = cache.pageSize || this.pageSize;
    this.isLoading = false;
    this.isSorting = false;
    this.recordCountSet.emit(this.totalRecords);
    this.dataLoaded.emit('AssignedInspectionsJanusComponent');
    this.detectChanges();
    return true;
  }

  private syncInspectorFromToolbarFilters(serviceData: any): void {
    const uiFilters = [
      ...(serviceData?.filterInputs || []),
      ...(serviceData?.attributeFilterInputs || [])
    ];

    const inspectorFilter = uiFilters.find(
      (f: any) =>
        String(f?.id || f?.field || f?.name || f?.title || '')
          .trim()
          .toLowerCase() === 'inspectorid'
    );

    if (!inspectorFilter) {
      return;
    }

    const selectedInspector = String(
      inspectorFilter?.options?.[0]?.searchText ||
        inspectorFilter?.options?.[0]?.filterText ||
        ''
    ).trim();

    if (!selectedInspector) {
      return;
    }

    this.setSelectedInspectorId(selectedInspector);
    this.persistUiStateToCache();
  }

  private restoreSortFromCache(): void {
    const sortCache = localStorage.getItem(this.SORT_CACHE_KEY);
    if (!sortCache) {
      return;
    }

    try {
      const parsed = JSON.parse(sortCache);
      this.sortField = parsed.sortField ?? 'inspectionAppNumber';
      this.sortDescending = parsed.sortDescending ?? true;
    } catch (e) {
      console.error('Invalid assigned inspections sort cache', e);
    }
  }

  private restoreUiStateFromCache(): void {
    const cache = localStorage.getItem(this.UI_STATE_CACHE_KEY);
    if (!cache) {
      return;
    }

    try {
      const parsed = JSON.parse(cache) || {};
      const mode = String(parsed?.viewMode || '').toLowerCase();
      this.viewMode = mode === 'map' ? 'map' : 'list';
      const inspectorId = String(parsed?.selectedInspectorId || '').trim();
      this.setSelectedInspectorId(inspectorId || 'ALL');
    } catch {
      localStorage.removeItem(this.UI_STATE_CACHE_KEY);
    }
  }

  private persistUiStateToCache(): void {
    const payload = JSON.stringify({
      viewMode: this.viewMode,
      selectedInspectorId: this.selectedInspectorId || 'ALL'
    });

    if (localStorage.getItem(this.UI_STATE_CACHE_KEY) === payload) {
      return;
    }

    localStorage.setItem(this.UI_STATE_CACHE_KEY, payload);
  }

  private setSelectedInspectorId(value: string): void {
    this.selectedInspectorId = value || 'ALL';
    this.inspectorDropdownData = {
      ...this.inspectorDropdownData,
      selectedValue:
        this.selectedInspectorId === 'ALL' ? null : this.selectedInspectorId
    };
  }

  private emitMapRefresh(): void {
    this.mapFilters$.next({
      inspectorId: this.selectedInspectorId,
      timestamp: Date.now()
    });
  }

  private safeFormatDate(dateStr: any, format: string): string {
    if (!dateStr) return '';
    const str = String(dateStr);

    // Ignore .NET default minimum dates
    if (
      str.includes('0001-01-01') ||
      str.includes('1970-01-01') ||
      str.includes('1969-12-31')
    )
      return '';

    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      if (d.getFullYear() <= 1) return ''; // Catch edge-case uninitialized dates
      return moment(d).format(format);
    } catch (e) {
      return '';
    }
  }

  private normalizeGeometryWkts(geometryWkts: any): string[] {
    if (Array.isArray(geometryWkts)) {
      return geometryWkts.filter(wkt => !!String(wkt || '').trim());
    }
    const singleWkt = String(geometryWkts || '').trim();
    return singleWkt ? [singleWkt] : [];
  }

  private normalizeInspection(rawInsp: any): any {
    const statusNormalized = String(rawInsp.inspectionStatus || '')
      .trim()
      .toLowerCase()
      .replace(/\s/g, '');
    const isPending = [
      'pendingschedule',
      'pendinginspection',
      'requested',
      'scheduled',
      'needed'
    ].includes(statusNormalized);

    return {
      id: rawInsp.inspectionId,
      inspectionAppNumber: rawInsp.inspectionApplicationNumber,
      appNo: rawInsp.applicationNumber,
      applicant: rawInsp.applicantName,
      appType: rawInsp.applicationType,
      appDate: this.safeFormatDate(
        rawInsp.applicationCreatedOnDate,
        'MM/DD/YYYY'
      ),
      applicantPhone: rawInsp.applicantPhoneNumber || '',
      parcelId: rawInsp.parcelID || '',
      // Only show completed date if the status is not pending
      inspectionCompleted: !isPending
        ? this.safeFormatDate(rawInsp.inspectionCompletedOnDate, 'MM/DD/YYYY')
        : '',
      inspector:
        rawInsp.inspectorName &&
        String(rawInsp.inspectorName)
          .trim()
          .toUpperCase() !== 'NULL'
          ? String(rawInsp.inspectorName).trim()
          : '',
      inspectionType: rawInsp.inspectionType || '',
      status: rawInsp.inspectionStatus || '',
      description: rawInsp.applicationDescription || '',
      requestedOnDate:
        rawInsp.requestedOnDate ||
        rawInsp.RequestedOnDate ||
        this.safeFormatDate(
          rawInsp.requestedOn || rawInsp.RequestedOn,
          'MM/DD/YYYY'
        ) ||
        '',
      scheduledOnDate:
        rawInsp.scheduledOnDate ||
        rawInsp.ScheduledOnDate ||
        this.safeFormatDate(
          rawInsp.scheduledOn || rawInsp.ScheduledOn,
          'MM/DD/YYYY'
        ) ||
        '',
      scheduledOnTime: rawInsp.scheduledOnTime || rawInsp.ScheduledOnTime || '',
      address: rawInsp.address || rawInsp.Address || '',
      city: rawInsp.city || rawInsp.City || '',
      // Map it for Scheduled Date specifically for Assigned Inspections mapping
      inspectionDate: this.safeFormatDate(
        rawInsp.inspectionCompletedOnDate,
        'MM/DD/YYYY'
      ),
      inspectionTime: this.safeFormatDate(
        rawInsp.inspectionCompletedOnDate,
        'h:mm A'
      ),
      geometryWkts: this.normalizeGeometryWkts(rawInsp.geometryWkts)
    };
  }

  private toMapData(row: any): any {
    const geoPt: any = row.geometryWkts?.length
      ? GeomUtils.WktToArcGisGeom(row.geometryWkts[0])
      : null;

    return {
      geometryWkts: row.geometryWkts,
      data: {
        id: row.id,
        appNo: row.appNo,
        permitNumber: row.appNo,
        inspectionAppNumber: row.inspectionAppNumber,
        inspectionType: row.inspectionType,
        status: row.status,
        inspector: row.inspector,
        appDate: row.appDate,
        inspectionCompleted: row.inspectionCompleted,
        inspectionDate: row.inspectionDate,
        inspectionTime: row.inspectionTime,
        lat: geoPt ? geoPt.y : null,
        lon: geoPt ? geoPt.x : null
      }
    };
  }

  private emitMapDataFromCurrentRows(request: MapDataRequest): void {
    let mapData = (this.data || [])
      .filter(
        row => Array.isArray(row.geometryWkts) && row.geometryWkts.length > 0
      )
      .map(row => this.toMapData(row));

    if (mapData.length === 0) {
      mapData = (this.pagedData || [])
        .filter(
          row => Array.isArray(row.geometryWkts) && row.geometryWkts.length > 0
        )
        .map(row => this.toMapData(row));
    }

    request.gimmeTheData$.emit(mapData);
  }

  public processInspectionActivity(
    workflowAppId: string,
    inspId: string,
    actionType: string,
    tabName: string,
    row: any
  ) {
    const appId = this.resolveApplicationId(null, row, {
      workflowApplicationId: workflowAppId
    });
    if (!appId) {
      this.toast.error('Application ID is missing, cannot load activity.');
      return;
    }

    this.workflowApplicationId = appId;
    this._workflowSvc
      .getInspectionActivity(appId, inspId, actionType)
      .subscribe(activityResp => {
        if (activityResp) {
          this.activity = this.setActivityApplicationId(activityResp, appId);
          this.activityForm = this._fb.group(
            ValidationService.createValidationGroup(this.activity)
          );
          this.openInline(row, tabName);
        } else {
          this.toast.info(
            'The action you requested is not available for this inspection.',
            'Action unavailable.'
          );
        }
      });
  }

  private syncFormToActivity() {
    if (!this.activityForm || !this.activity) return;

    const formValues = this.activityForm.getRawValue();
    const dynamicActivity = this.activity as any;

    if (dynamicActivity.model?.formLayoutModel?.columnEntities) {
      dynamicActivity.model.formLayoutModel.columnEntities.forEach(
        (col: any) => {
          const entity = col.entity;
          if (entity && formValues[entity.id] !== undefined) {
            const val = formValues[entity.id];
            if (entity.dataEntityTypeCode === 'date' && val) {
              entity.value = JSON.stringify({
                Value: new Date(val).toISOString(),
                HasTime: true,
                DSTFixed: null
              });
            } else if (entity.dataEntityTypeCode === 'list-data') {
              entity.selectedValue = val;
              entity.value = val;
            } else if (entity.dataEntityTypeCode === 'boolean') {
              entity.value = val ? 'True' : 'False';
            } else {
              entity.value = val;
            }
          }
        }
      );
    }
  }

  private getValidId(...ids: any[]): string {
    for (const id of ids) {
      if (id === undefined || id === null) continue;
      const safeId = String(id).trim();
      if (safeId && safeId !== 'undefined' && safeId !== 'null') {
        return safeId;
      }
    }
    return '';
  }

  private resolveApplicationId(
    details?: any,
    row?: any,
    activity?: any
  ): string {
    return this.getValidId(
      activity?.workflowApplicationId,
      activity?.WorkflowApplicationId,
      activity?.model?.inspection?.workflowApplicationId,
      activity?.model?.inspection?.inspectionApplicationId,
      activity?.model?.scheduleStepActivity?.workflowApplicationId,
      activity?.model?.completeStepActivity?.workflowApplicationId,
      details?.inspectionApplicationId,
      details?.InspectionApplicationId,
      details?.linkedApplicationId,
      details?.LinkedApplicationId,
      details?.applicationId,
      details?.ApplicationId,
      row?.workflowApplicationId,
      row?.inspectionApplicationId
    );
  }

  private setActivityApplicationId(activity: any, appId: string): any {
    if (!activity || !appId) return activity;
    activity.workflowApplicationId = activity.workflowApplicationId || appId;
    if (activity.model?.inspection) {
      activity.model.inspection.workflowApplicationId =
        activity.model.inspection.workflowApplicationId || appId;
      activity.model.inspection.inspectionApplicationId =
        activity.model.inspection.inspectionApplicationId || appId;
    }
    if (activity.model?.scheduleStepActivity) {
      activity.model.scheduleStepActivity.workflowApplicationId =
        activity.model.scheduleStepActivity.workflowApplicationId || appId;
    }
    if (activity.model?.completeStepActivity) {
      activity.model.completeStepActivity.workflowApplicationId =
        activity.model.completeStepActivity.workflowApplicationId || appId;
    }
    return activity;
  }

  onInspectionNumberClick(event: MouseEvent, row: any) {
    event.stopPropagation();
    this.openInline(row, 'documents');
  }

  openInline(row: any, tabName: string) {
    if (!this.table) return;

    this.activeInlineRow = row;
    this.table.expandRow(row);
    this.expandedComponent = InspectionsJanusViewDetailsComponent;
    this.expandedComponentInputs = (tableRow: any) => ({
      row: tableRow,
      initialTab: tabName,
      activity: this.activity,
      fetchActivity: (actionType: string) => {
        this._workflowSvc.getInspectionJanus(tableRow.id).subscribe(
          (inspectionDetails: any) => {
            const appId = this.resolveApplicationId(
              inspectionDetails,
              tableRow,
              this.activity
            );
            if (!appId) {
              this.toast.error(
                'Application ID is missing, cannot load activity.'
              );
              return;
            }
            this.workflowApplicationId = appId;
            this._workflowSvc
              .getInspectionActivity(appId, tableRow.id, actionType)
              .subscribe(res => {
                if (res) {
                  this.activity = this.setActivityApplicationId(res, appId);
                  this.activityForm = this._fb.group(
                    ValidationService.createValidationGroup(this.activity)
                  );
                  this.openInline(
                    tableRow,
                    actionType === 'scheduleInspection'
                      ? 'schedule'
                      : 'complete'
                  );
                } else {
                  this.toast.info(
                    'The action you requested is not available for this inspection.',
                    'Action unavailable.'
                  );
                }
              });
          },
          error => {
            console.error('Failed to fetch inspection details', error);
            this.toast.error('Failed to load inspection details.');
          }
        );
      },

      onScheduleSubmit: (_scheduleData: any) => {
        this.setInlineView(tableRow, 'schedule');
        if (_scheduleData) this.activity = _scheduleData;
        this.submitInspectionActivity(
          this.activity,
          'Inspection Scheduled Successfully',
          'Failed to update inspection schedule.'
        );
      },
      onCompleteSubmit: (_completeData: any) => {
        this.setInlineView(tableRow, 'complete');
        this.syncFormToActivity();
        if (_completeData) this.activity = _completeData;
        this.submitInspectionActivity(
          this.activity,
          'Inspection Completed Successfully',
          'Failed to complete inspection.'
        );
      },
      closeExpansion: () => {
        this.closeInlineRoute();
      }
    });
  }

  // The refresh after submit reopens the panel on the URL's view, which may still be View Summary.
  private setInlineView(row: any, view: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { view, id: row?.id },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private submitInspectionActivity(
    activityData: any,
    successMessage: string,
    failureMessage: string
  ) {
    this.syncFormToActivity();
    const appId = this.resolveApplicationId(
      null,
      this.activeInlineRow,
      activityData
    );
    if (!appId) {
      this.toast.error('Application ID is missing, cannot submit activity.');
      return;
    }
    // Removed this.closeInlineRoute(); to allow proceeding to the next step
    this.workflowApplicationId = appId;
    this.activity = this.setActivityApplicationId(activityData, appId);
    const decActivity = JsonNetDecycle.decycle(this.activity);

    // Call saveWorkflowActivity for Schedule step, submitWorkflowActivity for Complete step
    const isConsolidatedSchedule =
      activityData.type === 'consolidated-inspection-activity' &&
      activityData.submittedStep === InspectionStep.Schedule;
    const request$: any = isConsolidatedSchedule
      ? this._workflowSvc.saveWorkflowActivity(appId, decActivity)
      : this._workflowSvc.submitWorkflowActivity(appId, decActivity, false);

    request$.subscribe({
      next: (res: any) => {
        if (res && res.saveSuccess === false) {
          this.toast.error(res.message || failureMessage);
          return;
        }
        if (
          res &&
          !isConsolidatedSchedule &&
          res.activityWasCompleted === false
        ) {
          this.toast.error(res.message || failureMessage);
          return;
        }
        this.toast.success(successMessage);

        // Increase delay to allow backend CQRS read-models to fully update
        setTimeout(() => {
          this.fetchInspections(this.currentPage, true);
        }, 1500);
      },
      error: err => {
        console.error(err);
        this.toast.error('A network error occurred while submitting.');
        this.fetchInspections(this.currentPage, true);
      }
    });
  }

  submitScheduleStep() {
    this.submitInspectionActivity(
      this.activity,
      'Inspection Scheduled Successfully',
      'Failed to update inspection schedule.'
    );
  }

  submitCompleteStep() {
    this.submitInspectionActivity(
      this.activity,
      'Inspection Completed Successfully',
      'Failed to complete inspection.'
    );
  }

  public showTime(scheduledOn: any, inspection: any) {
    if (!scheduledOn) return false;
    const date = new Date(scheduledOn);
    if (inspection.scheduledOnIncludesTime === true) return true;
    if (inspection.scheduledOnIncludesTime === false) return false;
    return (
      date.getUTCHours() + date.getUTCMinutes() + date.getUTCSeconds() !== 0
    );
  }

  public getExportOptions(useExcelFormat: boolean): any {
    const serviceData = this.inspectionsJanusSvc.getData();
    let rawFilters = [
      ...(serviceData?.filterInputs ?? []),
      ...(serviceData?.attributeFilterInputs ?? [])
    ];

    rawFilters = rawFilters.filter((f: any) => {
      const key = String(f?.title || f?.field || f?.name || '')
        .trim()
        .toLowerCase();
      return (
        key !== 'inspectionmywork' &&
        key !== 'inspectorid' &&
        key !== 'inspector'
      );
    });

    if (this.selectedInspectorId && this.selectedInspectorId !== 'ALL') {
      rawFilters.push(
        new ItemSearchOptionField({
          title: 'inspectorId',
          options: [
            new ItemSearchOptionFieldOption({
              title: '',
              selected: true,
              filterText: '',
              searchText:
                this.selectedInspectorId === 'NULL'
                  ? 'NULL'
                  : this.selectedInspectorId,
              strOperator: 'is'
            })
          ]
        })
      );
    }

    const searchTextFilters = serviceData?.searchText || [];
    if (searchTextFilters.length > 0) {
      rawFilters.push(...searchTextFilters);
    }

    const mapExtent = this.viewMode === 'map' ? this.currentMapExtent : null;
    const exportColumns = this.exportColumns.length
      ? this.exportColumns
      : this.getExportableColumns().map(c => (c as any).apiKey as string);

    return {
      clientId: this.client?.id || this._context.client?.id || null,
      filters: rawFilters,
      additionalFilters: rawFilters,
      useExcelFormat: useExcelFormat,
      exportColumns: exportColumns,
      extent: mapExtent,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };
  }

  private getExportableColumns(): TableColumn[] {
    return this.columns.filter(c => !!String((c as any).apiKey || '').trim());
  }

  private detectChanges() {
    if (!this.isDestroyed) {
      this.ref.detectChanges();
    }
  }

  private closeInlineRoute(): void {
    this.clearInlineState();
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { view: null, id: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearInlineState(): void {
    this.expandedComponent = null;
    this.expandedComponentInputs = null;
    this.activeInlineRow = null;
    if (this.table) {
      this.table.expandedRow = null;
    }
  }
}
